import Channel from "./Channel";
import Ports from "./Ports";
import Point from "./Point";
import BaseObject from "./BaseObject";
import { getChildElement, getDrawingAreaPos, getScale } from "../modelViews/GraficElementUtils";
import { PageUsages } from "./PageUsages";
import { copyProperties, sortArrayByProperty } from "./utils";
import { PageUsage } from "./PageUsage";
import Connection, { ConnectionType, askToSyncConsumerChannelType } from "./Connection";
import BaseModel from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import { ChannelType, channelTypeInfo } from "./ChannelType";
import Room from "./Room";
import { PortInfo } from "./PortInfos";
import Logger from "../Logger";
import type Page from "./Page";
import type Scene from "./Scene";
import type Multiplexer from "./Multiplexer";
import Consumer from "./Consumer";
import { itx } from "../translationItx/itx";
import ButtonChannel from "./ButtonChannel";
import Floor from "./Floor";
import type Button from "./Button";
import type ErrorItems from "./ErrorItems";

const logger = new Logger();

const persistentProperyNames = ["id", "name", "address", "room", "center"]; // no itx

export default class ConnectableObject extends BaseModel {
  // persistent Properties

  public get isConnectableObject(): boolean {
    return true;
  }

  public get isActorChannel(): boolean {
    return false;
  }

  // -----------------------------------------------------------------------------
  // ports
  // -----------------------------------------------------------------------------
  _ports?: Ports;
  get ports(): Ports | undefined {
    return this._ports;
  }

  set ports(newValue: Ports | undefined) {
    if (this._ports === newValue) return;
    this.beforeChange("ports");
    this._ports = newValue;
    this.changed("ports");
  }

  // -----------------------------------------------------------------------------
  // center
  // -----------------------------------------------------------------------------
  _center = new Point(0, 0);
  get center(): Point {
    return this._center;
  }

  set center(newValue: Point) {
    if (this._center === newValue) return;
    this.beforeChange("center");
    this._center = newValue;
    this.changed("center");
  }

  // -----------------------------------------------------------------------------

  ///////////////////////////////////////////////////////////////////////////
  _channelType: ChannelType = ChannelType.Unknown;

  // -----------------------------------------------------------------------------
  // isDali
  // -----------------------------------------------------------------------------
  _isDali: boolean = false;

  get isDali(): boolean {
    return this._isDali ?? false;
  }
  set isDali(newValue: boolean) {
    this.beforeChange("isDali");
    this._isDali = newValue;

    this.changed("isDali");
  }

  // -----------------------------------------------------------------------------

  getChannelType(): ChannelType {
    return this._channelType; // allow scene.ts to return a hard coded channelType
  }

  get channelType(): ChannelType {
    return this.getChannelType();
  }

  set channelType(newValue) {
    this.beforeChange("channelType");
    this.setChannelType(newValue);

    this.changed("channelType");
  }

  setChannelType(newValue: ChannelType) {
    this._channelType = newValue;
  }

  ///////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // image
  // -----------------------------------------------------------------------------
  _image: string = "";
  get image(): string {
    return this._image;
  }

  set image(newValue: string) {
    this.beforeChange("image");
    this._image = newValue;

    this.changed("image");
  }

  // persistent sub collections

  // -----------------------------------------------------------------------------
  // pageUsages
  // -----------------------------------------------------------------------------
  _pageUsages = new PageUsages();
  get pageUsages(): PageUsages {
    return this._pageUsages;
  }

  set pageUsages(newValue: PageUsages) {
    if (this._pageUsages === newValue) return;
    this.beforeChange("pageUsages");
    this._pageUsages = newValue;
    this.changed("pageUsages");
  }

  // -----------------------------------------------------------------------------

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyProperties(data, this);
  }

  getIconUrl(): string | undefined {
    return this.image;
  }

  //////////////////////////////////////////////////////////////////
  delete(): Array<BaseObject> {
    this.isDeleting = true;

    let res = new Array<BaseObject>();

    this.connections.forEach((connection) => {
      res = res.concat(connection.delete());
    });

    res = res.concat(super.delete());
    return res;
  }

  getPersistentProperties(): string[] {
    return [
      "ports", // no itx
      "center", // no itx
      "pageUsages", // no itx
      "_image", // no itx
      "channelType", // no itx
      "errorState", // no itx
      "_isDali", // no itx
      ...super.getPersistentProperties(),
    ];
  }

  ////////////////////////////////////////////////////////////////////////////
  //non persistent properties

  color: string = "var(--text-color)";

  private _isInEditMode = false;
  connections = new BaseModelMap<Connection>(Connection, undefined, undefined, false);

  getConnection(type: ConnectionType): Connection | undefined {
    let res: Connection | undefined;

    this.connections.forEach((connection) => {
      if (connection.type == type) {
        res = connection;
      }
    });
    return res;
  }

  isConnected(type: ConnectionType): boolean {
    return this.getConnection(type) != undefined;
  }

  isConnectedWith(partner: ConnectableObject): boolean {
    let res = false;
    this.connections.forEach((connection) => {
      if (connection.getPartner(this) == partner) {
        res = true;
      }
    });
    return res;
  }

  //////////////////////////////////////////////////////////////////////

  onConnect(connection: Connection) {
    // to use override in subclass
  }

  onDisconnect(connection: Connection) {
    // to use override in subclass
  }

  //////////////////////////////////////////////////////////////////////

  drawingItemColor(): string {
    return "var(--drawingItemColor-" + this.className + ")";
  }

  ////////////////////////////////////////////////////////////////////////////

  setIsInEditMode(newValue: boolean, pageId: string) {
    this._isInEditMode = newValue;

    /// MWE 27.11.2022 break cylic reference !?!?!? however. it works
    // const c: any = connections;
    const w: any = window;
    w.connections.updateOneDevice(pageId, this);
  }
  public get isInEditMode(): boolean {
    return this._isInEditMode;
  }

  generateNewNameFromTag() {
    super.generateNewNameFromTag();

    let name = this.name;
    if (this.isDali) {
      name = itx("channelType.dali", "DALI") + " " + name;
    } else if (this.channelType != ChannelType.Unknown) {
      if (!channelTypeInfo(this.channelType).isSupply && !channelTypeInfo(this.channelType).isLan) {
        name = itx("channelType." + this.channelType, this.channelType) + " " + name;
      } else {
        name = itx("channelType." + this.channelType, this.channelType);
      }
    }
    this.name = name;
  }

  getParentPos = (): any | null => {
    const parentPos = document.getElementById("mainDrawingArea")?.getBoundingClientRect();

    return parentPos;
  };

  getPortPosition(document: Document, mainPageId: string, connection: Connection): PortInfo | undefined {
    //  MWE 27.9.2025
    //
    //  dieser Code funktioniert NICHT in Rooms von FloorPlans
    //  daher zunächst noch einmal totgelegt.
    //
    if (this.className == "Button" || this.isConsumer) {
      // clone to avoid mutating this.position
      const info = new PortInfo(new Point(this.position.x, this.position.y), undefined);
      const mainPage = state.objectRegistry.getObject(mainPageId) as Page;
      const room = this.room;

      if (!room) {
        return undefined;
      }

      if (mainPage?.isFloorPlan) {
        if (!room.isFloorGeneralRoom) {
          //
          // we render a floorPlan
          //
          // and the consumer or button is inside a room
          // we have to transform the point for roomSpace to floorSpace
          //
          const roomtoFloorDrawingTransform = room?.roomtoFloorDrawingTransform;
          if (roomtoFloorDrawingTransform) {
            info.point = roomtoFloorDrawingTransform.transformPoint(info.point);
            // check also if parent of room is this floor
            if (room.parent != mainPage.parent) {
              return undefined;
            }
          } else {
            // if the room is not placed in the floor, we have no legal position
            return undefined;
          }
        }
      } else {
        //
        // we render a roomPlan
        //

        if (room.isFloorGeneralRoom) {
          //
          // wenn dieses Objekt zur Etage gehört
          // hat es keine legale Position im Raumplan
          //
          return undefined;
        }

        //
        // Im Raumplan müssen wir prüfen ob dieses Object in einen anderen Raum liegt
        // wenn dem so ist,
        // müssen wir "undefined" zurückgeben, da es ja keine legale Position in diesem Raumplan hat
        //
        else if (room != mainPage.parent) {
          return undefined;
        }
      }

      // actor-channel connections arrive slightly above center; button-channel at center
      if (this.isButton && connection.type === ConnectionType.Channel) {
        info.point = new Point(info.point.x, info.point.y - 5);
      }

      return info;
    }

    const elmPage = document.getElementById(mainPageId);
    const elmDrawingArea = elmPage?.querySelector(".mainDrawingArea") as HTMLElement;
    const elmPane = elmPage?.closest(".pane");

    if (elmDrawingArea && elmPage && elmPane) {
      const parentPos = getDrawingAreaPos(elmDrawingArea); // this.getParentPos();
      if (parentPos) {
        const scale = getScale(elmDrawingArea);

        const elmChannel = elmPane.querySelector(`[id='${this.id}']`);
        if (elmChannel) {
          let dir: number | undefined;

          let elmPort = elmChannel.querySelector(`.port`);
          const elmPortL = elmChannel.querySelector(`.portL`) as HTMLElement;
          const elmPortR = elmChannel.querySelector(`.portR`) as HTMLElement;

          if (elmPortL && !elmPortR) {
            elmPort = elmPortL;
            dir = 180;
          } else if (!elmPortL && elmPortR) {
            elmPort = elmPortR;
            dir = 0;
          } else if (elmPortL && elmPortR) {
            const rLeft = elmPortL.getBoundingClientRect();
            const rRight = elmPortR.getBoundingClientRect();

            let partner: ConnectableObject | undefined;
            if (connection.toDevice == this) {
              partner = connection.fromDevice;
            } else {
              partner = connection.toDevice;
            }

            if (partner) {
              const elmOther = elmPane.querySelector(`[id='${partner.id}']`);
              if (elmOther) {
                const rOther = elmOther.getBoundingClientRect();
                if (rOther.left < rLeft.left) {
                  elmPort = elmPortL;
                  dir = 180;
                } else {
                  elmPort = elmPortR;
                  dir = 0;
                }
              }
            }
          }

          if (elmPort) {
            const r1 = elmPort.getBoundingClientRect();
            const r = { left: r1.left, top: r1.top, width: r1.width, height: r1.height };
            if (r) {
              const elmScrollContainer = elmPort?.closest(".scrollcontainer");
              if (elmScrollContainer) {
                const rScroll = elmScrollContainer.getBoundingClientRect();
                if (rScroll) {
                  if (r.top > rScroll.bottom) {
                    r.top = rScroll.bottom;
                    r.height = 0;
                  }
                  if (r.top < rScroll.top) {
                    r.top = rScroll.top;
                    r.height = 0;
                  }
                }
              }

              //
              // Limit to scrollcontainer
              //

              const center = new Point(r.left - parentPos.x + r.width / 2, r.top - parentPos.y + r.height / 2);

              center.y = center.y / scale + elmDrawingArea.scrollTop;
              center.x = center.x / scale;

              return new PortInfo(center, dir);
            }
          }
        }
      }
    }

    return undefined;
  }

  setPageUsage(pageId: string, pageUsage: PageUsage) {
    this.pageUsages = new PageUsages();

    pageUsage.pageId = pageId;
    this.pageUsages.set(pageId, pageUsage);
  }

  getPageUsage(pageId: string): PageUsage {
    //
    // pageUsage is a bit dummy for now
    //
    // we have allways only one reference for one page
    // without managing the page id,
    // witch maybe wrong after copy or move
    //
    let res = this.pageUsages.get(pageId);
    if (res) {
      return res;
    }

    res = undefined;
    if (this.pageUsages.size == 1) {
      const v = this.pageUsages.entries().next()?.value;
      if (v) {
        res = v[1];
      }
    }

    if (res === undefined) {
      res = new PageUsage({ pageId: pageId });
      this.pageUsages.set(pageId, res);
    }
    res.pageId = pageId;
    return res;
  }

  addConnection(connection: Connection) {
    this.connections.add(connection);
  }

  removeConnection(connection: Connection) {
    this.connections.remove(connection);
  }

  updateConnectionEndpoints() {
    // allow redraw, to get correct port position in collapsed mode
    setTimeout(() => {
      this.connections.forEach((connection) => connection.updateEndPoints());
    }, 100);
  }

  isConnectable(
    connectionType: ConnectionType,

    partnerObject: ConnectableObject,
    checkForConnection?: Connection,
  ): boolean {
    logger.error("isConnectable not implemented in subclass");

    return false;
  }

  private xHighLightConnections(hover: boolean, active: boolean, type: ConnectionType | undefined) {
    logger.log("highLightConnections");

    const toTop = Array<HTMLElement>();
    const pages = new Map<string, Page>();

    this.connections.forEach((connection) => {
      const page = connection.parent;
      if (page) {
        pages.set(page.id, page as Page);
      }

      if (type == undefined || connection.type == type) {
        if (hover) {
          connection.setIsHovered(active);
        } else {
          connection.setIsHighlighted(active);
        }

        if (connection.type == ConnectionType.Scene || this.className == "Multiplexer") {
          const consumer = connection.getConsumer();
          if (consumer) {
            consumer.connections.forEach((connection) => {
              if (connection.type == ConnectionType.Channel) {
                if (hover) {
                  connection.setIsHovered(active);
                } else {
                  connection.setIsHighlighted(active);
                }
              }
            });
          }
        }
      }
    });

    // bring highlighted elements to top
    if (active) {
      toTop.map((elm) => {
        const parent = elm.parentElement;
        elm.remove();
        parent?.appendChild(elm);
      });
    }
  }

  highLightConnections(hover: boolean, type: ConnectionType | undefined, highlightedObjId?: string) {
    this.xHighLightConnections(hover, true, type);
  }

  dehighLightConnections(hover: boolean) {
    this.xHighLightConnections(hover, false, undefined);
  }

  getConnectedLockConsumers(): Array<Consumer> {
    const res: Array<Consumer> = new Array<Consumer>();
    this.connections.forEach((connection) => {
      const partner = connection.getPartner(this);
      if (partner) {
        if (partner.className == "Consumer" && partner.channelType == ChannelType.Lock) {
          res.push(partner as Consumer);
        }
      }
    });

    //
    // in C1-Projecten brauchen wir nicht unbedingt eine Verbindung zu jedem Lock-Consumer innerhalb der Gruppe.
    // die nehmen wir impliziet an
    //

    if (this.isConsumer) {
      const parentGroup = (this as any as Consumer).parentGroup;
      if (parentGroup) {
        parentGroup.consumers.forEach((consumer) => {
          if (consumer.channelType == ChannelType.Lock) {
            if (!res.includes(consumer)) {
              res.push(consumer);
            }
          }
        });
      }
    }

    return res;
  }

  getConnectedChannel(): Channel | undefined {
    let res: Channel | undefined = undefined;
    this.connections?.forEach((connection) => {
      const partner = connection.getPartner(this);
      if (partner?.isActorChannel) {
        res = partner as Channel;
      }
    });

    return res;
  }

  get room(): Room | undefined {
    return this.parentRoom;
  }

  setSelected(newValue: boolean) {
    super.setSelected(newValue);
    if (newValue) {
      this.highLightConnections(false, undefined);
    } else {
      this.dehighLightConnections(false);
    }
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      const newPageUsages = new PageUsages();
      this.pageUsages.forEach((pageUsage) => {
        pageUsage.mapAfterCopy();
        newPageUsages.set(pageUsage.pageId, pageUsage);
      });
      this.pageUsages = newPageUsages;
    }
  }
  updatePageUsageAfterCopy() {
    if (this.parent) {
      if (this.className == "Button" || this.className == "Consumer") {
        const room = this.parent as Room;
        const page = room.page;

        const sourceObject = this.state.objectRegistry.getObject(this.sourceId);
        const copyPasteOnSamePage = sourceObject && sourceObject.parent == this.parent;

        if (page) {
          let pageUsage = this.pageUsages.get(page.id);
          if (pageUsage == undefined) {
            pageUsage = this.pageUsages.get(""); // dont do anything
            this.pageUsages.forEach((pageUsageLast) => {
              pageUsage = pageUsageLast;
            });
          }
          if (pageUsage) {
            this.pageUsages.delete(pageUsage.pageId);
            pageUsage.pageId = page.id;
            this.pageUsages.set(pageUsage.pageId, pageUsage);
          }
          // handle copy from one page to an other page

          if (pageUsage) {
            let pPX = new Point(pageUsage.position.x, pageUsage.position.y);
            if (page) {
              if (page.pointAlreadyUsed(pPX)) {
                pPX = page.findFreePositionForNewObject(pPX);
              }
            }
            pageUsage.position = pPX;
          }
        }
      }
    } else {
      logger.error("parent undefined");
    }
  }

  connectObject(
    page: Page,
    connectableObject: ConnectableObject,
    type: ConnectionType,
    askToSyncChannelType: boolean = true,
  ): Connection {
    const c = new Connection({}, page);

    c.type = type;

    /// from = consumer
    c.fromId = connectableObject.id;

    /// to = channel
    c.toId = this.id;

    c.addToConnectableObjects();
    c.addToPage(page);

    this.parent?.raiseOnChange(); // to update drawing in compact view

    // allow redraw, to get correct port position in collapsed mode
    setTimeout(() => {
      c.updateEndPoints();
    }, 100);

    if (type == ConnectionType.Channel) {
      askToSyncConsumerChannelType(this as unknown as Channel, connectableObject as Consumer, askToSyncChannelType);
    }

    return c;
  }

  getConnectedScenes(): Array<Scene> {
    const res = new Array<Scene>();
    this.connections.forEach((connection) => {
      const partner = connection.getPartner(this);
      if (partner?.className == "Scene") {
        res.push(partner as Scene);
      }
    });
    return sortArrayByProperty(res, "name"); // no itx
  }

  getConnectedMultiplexers(): Array<Multiplexer> {
    const res = new Array<Multiplexer>();
    this.connections.forEach((connection) => {
      const partner = connection.getPartner(this);
      if (partner?.className == "Multiplexer") {
        res.push(partner as Multiplexer);
      }
    });
    return res;
  }

  getConnectedButtonChannels(): Array<ButtonChannel> {
    const res = new Array<ButtonChannel>();
    this.connections.forEach((connection) => {
      const partner = connection.getPartner(this);
      if (partner?.className == "ButtonChannel" && !partner.isActorChannel) {
        res.push(partner as ButtonChannel);
      }
    });
    return res;
  }

  getConnectionWith(searchPartner: ConnectableObject): Connection | undefined {
    let res: Connection | undefined;

    if (!searchPartner) {
      console.error("searchPartner == null");
      return undefined;
    }

    this.connections.forEach((connection) => {
      const connectionPartner = connection.getPartner(this);

      if (searchPartner == connectionPartner) {
        res = connection;
      } else if (searchPartner?.isButton && connectionPartner?.isButtonChannel) {
        //
        // lasse die suche von Consumer zu Button zu.
        // wobei die Verbindung beim Button dann beim ButtonChannel ankommen
        //
        if (connectionPartner.parent == searchPartner) {
          res = connection;
        }
      }
    });

    return res;
  }

  get position(): Point {
    const room = this.parentRoom;
    const floor = room?.parentFloor;
    if (room && floor) {
      let page: Page | undefined;
      if (room.isFloorGeneralRoom) {
        page = floor.getOrCreatePage();
      } else {
        page = room.getOrCreatePage();
      }

      if (page) {
        return this.getPageUsage(page.id).position;
      }
    }

    return new Point(0, 0);
  }

  set position(newValue: Point) {
    let page: Page | undefined;

    if (this.parent && (this.parent?.className == "Room" || this.parent?.className == "Floor")) {
      page = (this.parent as Room | Floor).getOrCreatePage();
    }
    if (this.parent?.className == "Page") {
      page = this.parent as Page;
    }

    if (page) {
      this.beforeChange("pageUsages");
      this.getPageUsage(page.id).position = newValue;
      this.changed("pageUsages");
    }
  }

  //
  // temporary position in floorSpace for modifications of room psoition and size
  //
  pFloorFrozen?: Point;

  //
  // returns undefined if the room is not placed in the floor
  //
  get positionInFloorSpace(): Point | undefined {
    const p = this.position;
    if (this.parentRoom?.isFloorGeneralRoom) {
      return p;
    } else {
      const pFloor = this.parentRoom?.roomtoFloorDrawingTransform?.transformPoint(p);
      return pFloor || new Point(0, 0);
    }
  }

  set positionInFloorSpace(newValue: Point) {
    let p: Point | undefined = newValue;

    if (!this.parentRoom?.isFloorGeneralRoom) {
      p = this.parentRoom?.roomtoFloorDrawingTransform?.transformPointInvers(newValue);
    }

    if (p) {
      this.position = p;
    }
  }

  moveToNewParent(newParent: BaseModel): boolean {
    let newPlacementInNewParent = this.position;
    if (newParent.className == "Room" && this.parent?.className == "Room") {
      const newRoomParent = newParent as Room;
      const oldRoomParent = this.parent as Room;
      if (newRoomParent.isFloorGeneralRoom && !oldRoomParent.isFloorGeneralRoom) {
        newPlacementInNewParent = this.positionInFloorSpace || newPlacementInNewParent;
      } else if (!newRoomParent.isFloorGeneralRoom && oldRoomParent.isFloorGeneralRoom) {
        newPlacementInNewParent =
          newRoomParent.roomtoFloorDrawingTransform?.transformPointInvers(newPlacementInNewParent) ||
          newPlacementInNewParent;
      }
    }
    const res = super.moveToNewParent(newParent);
    this.position = newPlacementInNewParent;
    return res;
  }

  get defaultImageSrc(): string {
    return "";
  }

  getDrawingElement(mainPage: Page): HTMLElement | null {
    const itemElm = document.querySelector(`[id="pane_${mainPage.id}"] [id="${this.id}"]`) as HTMLElement;
    return itemElm;
  }

  getScreenPosition(mainPage: Page): Point {
    const itemElm = this.getDrawingElement(mainPage);
    if (itemElm) {
      const r = itemElm.getBoundingClientRect();
      const pScreen = new Point(r.left + r.width / 2, r.top + r.height / 2);
      return pScreen;
    }
    return new Point(0, 0);
  }

  moveConnectionsToNewPage(oldPage: Page, newPage: Page): boolean {
    const connections = this.connections.asArray();
    connections.forEach((connection) => {
      oldPage.connections.remove(connection);
      newPage.connections.add(connection);
      connection.parent = newPage;
    });
    return true;
  }

  moveToRoom(targetRoom: Room): boolean {
    const pFloor = this.positionInFloorSpace;

    const currentRoom = this.parentRoom;

    if (pFloor && currentRoom && targetRoom) {
      if (this.isConsumer) {
        currentRoom?.consumers.remove(this as any as Consumer);
        targetRoom?.consumers.add(this as any as Consumer);
        this.parent = targetRoom;
        //
        // calculates position automatically in roomSpace with the transformation of the new room
        //
        this.positionInFloorSpace = pFloor;
        this.raiseOnChange("positionInFloorSpace");

        return this.moveConnectionsToNewPage(currentRoom.getOrCreatePage(), targetRoom.getOrCreatePage());
      }

      if (this.isButton) {
        currentRoom?.buttons.remove(this as any as Button);
        targetRoom?.buttons.add(this as any as Button);
        this.parent = targetRoom;

        //
        // see above
        //
        this.positionInFloorSpace = pFloor;
        this.raiseOnChange("positionInFloorSpace");

        return this.moveConnectionsToNewPage(currentRoom.getOrCreatePage(), targetRoom.getOrCreatePage());
      }
    }

    return false;
  }

  public get errors(): ErrorItems | undefined {
    let err1 = super.errors;

    this.connections.forEach((connection) => {
      //
      // use _forbidden to avoid extensive calculations here
      // _forbidden should be once calculated in Connection.checkForErrors
      //
      if (connection._forbidden) {
        err1 ||= {};
        err1.invalidConnection = "";
      }
    });
    return err1;
  }

  public set errors(value: ErrorItems | undefined) {
    this.beforeChange("errors");
    super.errors = value;

    this.changed("errors");
  }

  consistencyCheck(parent: BaseModel) {
    //
    // MWE 30.10.2025
    // invalidConnection are now handled dynamic
    //
    if (this.hasErrors && this.errors) {
      delete this.errors?.invalidConnection;
    }
  }

  getImgSrc(): string | undefined {
    const consumerOrButton = this as any as Consumer | Button;

    let imgSrc: string | undefined = undefined;

    if (consumerOrButton.isButton) {
      //
      // Button and Sensor
      //
      const button = consumerOrButton as Button;
      imgSrc = button.frontImage;
      imgSrc ||= button.defaultImageSrc;
      return imgSrc;
    } else if (consumerOrButton.isConsumer) {
      //
      // Consumer
      //
      imgSrc = consumerOrButton.image || undefined;
    }

    return imgSrc;
  }

  unlinkConnectionWith(partner: ConnectableObject | undefined) {
    if (partner) {
      const connection = this.connections.findFirst((c) => c.getPartner(this) == partner);
      if (connection) {
        connection?.delete();
        const lastName = this.name;
        this.name = "";
        this.raiseOnChange("connections");
        partner.raiseOnChange("connections");
      }
    }
  }
}
